// Shared UI primitives.
//
// Seven views were about to need the same form fields, the same slide-over, and
// the same "fetch a list of records and refresh it after a write". Written once
// here so a fix to date handling or error display lands everywhere at the same
// time, rather than in whichever view someone remembered.

import {
  useCallback, useEffect, useState, type ReactNode, type InputHTMLAttributes,
} from 'react';
import { listRecords, createRecord, updateRecord, deleteRecord, ApiError } from './api.js';
import type { RecordType } from './api.js';

// --- data -------------------------------------------------------------------

type Filters = Record<string, string | number | boolean | null | undefined>;

/**
 * A list of records, with the writes that change it.
 *
 * Every mutation refetches rather than patching local state. Slightly more
 * network, dramatically fewer bugs: the database computes defaults, triggers,
 * and generated columns, and a hand-merged local copy drifts from all three.
 */
export function useRecords<T extends { id: string }>(type: RecordType, filters: Filters = {}) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(filters);

  const load = useCallback(async () => {
    try {
      const r = await listRecords<T>(type, JSON.parse(key) as Filters);
      setRows(r.records);
      setError(null);
    } catch (e) {
      // NEVER pretend an empty list. setRows([]) here made a failed read
      // indistinguishable from "you have no deals / no contacts / nothing
      // staged" — four screens rendered confident emptiness over an outage.
      // Stale rows beat fake-empty; null means the caller can say "couldn't
      // load" instead of "nothing here".
      setRows((prev) => prev ?? null);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [type, key]);

  useEffect(() => { void load(); }, [load]);

  return {
    rows, error, reload: load,
    async create(data: Record<string, unknown>) {
      const r = await createRecord<T>(type, data);
      await load();
      return r.record;
    },
    async update(id: string, patch: Record<string, unknown>) {
      const r = await updateRecord<T>(type, id, patch);
      await load();
      return r.record;
    },
    async remove(id: string) {
      await deleteRecord(type, id);
      await load();
    },
  };
}

// --- formatting -------------------------------------------------------------

export function money(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n)) return '—';
  return n >= 1000
    ? `$${Math.round(n).toLocaleString('en-US')}`
    : `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function shortDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "3 days ago" for a timeline, where the exact timestamp is rarely the point. */
export function ago(v: string | null | undefined): string {
  if (!v) return '';
  const then = new Date(v).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 31) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`;
}

/** JSONB arrays arrive as arrays, but a hand-edited row can hold a string. */
export function asArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p.map(String) : [v];
    } catch { return v.split(',').map((s) => s.trim()).filter(Boolean); }
  }
  return [];
}

// --- form fields ------------------------------------------------------------

export function Field({ label, hint, children }: {
  label: string; hint?: string; children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

type TextProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string };

export function Text({ label, hint, ...rest }: TextProps) {
  return <Field label={label} hint={hint}><input {...rest} /></Field>;
}

export function Area({ label, hint, rows = 3, value, onChange, placeholder }: {
  label: string; hint?: string; rows?: number; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <textarea rows={rows} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function Pick({ label, hint, value, onChange, options, allowEmpty = true }: {
  label: string; hint?: string; value: string; onChange: (v: string) => void;
  options: readonly { value: string; label: string }[]; allowEmpty?: boolean;
}) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {allowEmpty && <option value="">—</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

/**
 * A list of short strings, entered one at a time.
 *
 * Used for every JSONB array in the app — ICP industries, persona titles, proof
 * points. A comma-separated text box was the alternative and it loses any value
 * containing a comma, which job titles do constantly ("VP, Marketing").
 */
export function Chips({ label, hint, values, onChange, placeholder }: {
  label: string; hint?: string; values: string[];
  onChange: (v: string[]) => void; placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <Field label={label} hint={hint}>
      <div className="chipsfield">
        {values.map((v) => (
          <span className="chiptag" key={v}>
            {v}
            <button type="button" aria-label={`Remove ${v}`}
              onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
          </span>
        ))}
        <input
          value={draft} placeholder={placeholder ?? 'Type and press Enter'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
            // Backspace on an empty box removes the last chip — the behaviour
            // every tag input has, and its absence feels broken.
            if (e.key === 'Backspace' && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={add}
        />
      </div>
    </Field>
  );
}

// --- chrome -----------------------------------------------------------------

/** Slide-over for create and edit. Escape closes; the backdrop closes. */
export function Drawer({ title, subtitle, onClose, children, footer, wide, footerTone }: {
  title: string; subtitle?: string; onClose: () => void;
  children: ReactNode; footer?: ReactNode;
  /** Composing needs room the standard drawer doesn't have. */
  wide?: boolean;
  /**
   * 'spend' turns the foot navy — the one dark beat on a light surface, used
   * only where the next click costs real money. Dark is an interruption here,
   * not a theme: it appears when there is something to decide and is absent
   * the rest of the time. Same treatment as .importbar, which it replaced.
   */
  footerTone?: 'spend';
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className={`drawer${wide ? ' drawer-wide' : ''}`} role="dialog"
        aria-modal="true" aria-label={title}>
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="meta">{subtitle}</p>}
          </div>
          <button className="iconbtn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && (
          <footer className={`drawer-foot${footerTone ? ` ${footerTone}` : ''}`}>
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}

export function Empty({ title, hint, action }: {
  title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="emptystate">
      <p className="emptystate-title">{title}</p>
      {hint && <p className="meta">{hint}</p>}
      {action && <div className="actions">{action}</div>}
    </div>
  );
}

/** Page header: title, optional count, and the one primary action. */
export function PageHead({ title, count, sub, action }: {
  title: string; count?: number; sub?: string; action?: ReactNode;
}) {
  return (
    <header className="pagehead">
      <div>
        <h1>{title}{count !== undefined && <span className="count"> {count}</span>}</h1>
        {sub && <p className="meta">{sub}</p>}
      </div>
      {action}
    </header>
  );
}

/** Inline error that doesn't move the page around when it appears. */
export function Err({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="error" role="alert">{children}</p>;
}

/**
 * A collapsible section whose HEADER CARRIES THE ANSWER.
 *
 * This replaced eight tabs on the account record. Tabs hide nouns — "decisions",
 * "documents", "sequence" — and a rep does not scan for nouns, they scan for
 * state. A tab labelled "sequence" tells you a sequence exists somewhere; a fold
 * headed "Sequence · draft · 2 of 18 written · not armed" has already answered
 * the question you opened the tab to ask, and most of the time you never expand
 * it at all.
 *
 * `tone` marks the ones that want you. `defaultOpen` is for whatever the record's
 * next action points at — the page should open on the thing you came to do.
 */
export function Fold({ title, id, summary, count, tone, defaultOpen = false, children }: {
  id?: string;
  title: string;
  summary?: ReactNode;
  count?: number | null;
  tone?: 'warn' | 'ok' | null;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-fold={id ?? title.toLowerCase().replace(/\s+/g, '-')}
      className={`fold ${open ? 'open' : ''} ${tone ? `fold-${tone}` : ''}`}>
      <button className="fold-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="fold-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="fold-title">{title}</span>
        {count !== null && count !== undefined && <span className="tcount">{count}</span>}
        <span className="fold-summary">{summary}</span>
      </button>
      {open && <div className="fold-body">{children}</div>}
    </section>
  );
}
